import axios from 'axios';
import { PATH_AUTH } from '../routes/paths';
import isAuthExemptUrl from './isAuthExemptUrl';

const axiosInstance = axios.create({ baseURL: process.env.HOST_API_KEY || '' });

// Set once the first 401 triggers a redirect. Concurrent requests (SWR
// revalidation + a click) can all fail together; only one should navigate.
let isRedirectingToLogin = false;

// Same effect as setSession(null) in ./jwt. Inlined because jwt.js imports
// this module, and importing it back here would be a circular import.
const clearSessionAndRedirectToLogin = () => {
  if (isRedirectingToLogin) {
    return;
  }
  isRedirectingToLogin = true;

  localStorage.removeItem('accessToken');
  delete axiosInstance.defaults.headers.common.Authorization;

  // `from` is the query RegisterGuard already uses to return the user after
  // login. Full reload (not router.push) so JWTContext re-initializes with
  // user: null.
  const from = window.location.pathname + window.location.search;
  const params = new URLSearchParams({ reason: 'expired', from });
  window.location.assign(`${PATH_AUTH.login}/?${params.toString()}`);
};

// Exported for tests only.
export const shouldRedirectOn401 = (error) => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (error?.response?.status !== 401) {
    return false;
  }
  // A 401 from login / cert / reset-password means bad credentials, not an
  // expired session. Same if we are already on an auth page.
  if (isAuthExemptUrl(error.config?.url)) {
    return false;
  }
  if (window.location.pathname.startsWith(PATH_AUTH.root)) {
    return false;
  }
  return true;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (shouldRedirectOn401(error)) {
      clearSessionAndRedirectToLogin();
    }
    // Always reject so callers' catch blocks keep working.
    return Promise.reject(error || 'Something went wrong');
  }
);

export default axiosInstance;
